import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  styled
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { fetchProfiles, perfilesCacheados } from '../utils/api';

// Volver a la galeria devuelve a la altura donde estabas, no al principio.
const CLAVE_SCROLL = 'scroll-galeria';

const recordarScroll = () => {
  try {
    sessionStorage.setItem(CLAVE_SCROLL, String(window.scrollY));
  } catch {
    /* modo privado: sin recordar scroll se vuelve arriba, no es grave */
  }
};

const scrollRecordado = () => {
  try {
    const y = sessionStorage.getItem(CLAVE_SCROLL);
    return y === null ? null : Number(y);
  } catch {
    return null;
  }
};

// Escalera de tinta sobre canvas oscuro. Las tres alturas viven aqui para que
// ningun componente vuelva a multiplicar opacidades con alpha().
const INK = {
  primaria: 'rgba(255, 255, 255, 0.92)',
  secundaria: 'rgba(255, 255, 255, 0.56)',
  borde: 'rgba(255, 255, 255, 0.10)',
  hover: 'rgba(255, 255, 255, 0.09)',
  media: 'rgba(255, 255, 255, 0.06)'
};

// Inclinacion maxima del efecto 3D, en grados, medida desde el centro hasta
// cualquier borde. Pasando de ~16 la portada se lee como un naipe girando y
// deja de parecer una foto; 12 se nota bien sin cruzar esa linea.
const INCLINACION = 12;

// Acercamiento al inclinar. Refuerza la sensacion de que la portada se levanta
// hacia el puntero en vez de solo girar sobre si misma.
const ACERCAMIENTO = 1.04;

// El puntero recorre de -0.5 a 0.5 sobre cada eje, asi que hay que duplicar
// para que INCLINACION sea el angulo real en el borde y no la mitad.
const FACTOR = INCLINACION * 2;

// La portada es el ancla visual: ocupa todo el ancho de la celda en formato
// retrato. El resto de la tarjeta solo la acompana.
const Portada = styled(Box)({
  position: 'relative',
  width: '100%',
  aspectRatio: '4 / 5',
  overflow: 'hidden',
  borderRadius: 8,
  backgroundColor: INK.media,
  // rotateX/rotateY y scale son propiedades compuestas: la animacion corre en
  // el compositor y no dispara layout ni paint.
  // Cuanto menor la perspectiva, mas pronunciada la fuga: 700px da volumen
  // real donde 900 se quedaba en un ladeo plano.
  transform:
    'perspective(700px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) scale(var(--z, 1))',
  transformStyle: 'preserve-3d',
  backfaceVisibility: 'hidden',
  transition: 'transform 140ms ease-out',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  '@media (prefers-reduced-motion: reduce)': {
    transform: 'none',
    transition: 'none'
  }
});

const Coleccion = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  width: '100%',
  cursor: 'pointer',
  position: 'relative',
  borderRadius: 8,
  outlineOffset: 4,
  '&:hover .acciones, &:focus-visible .acciones, &:focus-within .acciones': {
    opacity: 1
  }
});

const Acciones = styled(Box)({
  flexShrink: 0,
  zIndex: 2,
  transition: 'opacity 160ms ease',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  // En un puntero fino se revela al pasar por encima; en tactil no hay hover,
  // asi que queda visible pero discreto.
  '@media (hover: hover) and (pointer: fine)': { opacity: 0 },
  '@media (hover: none)': { opacity: 1 }
});

// El efecto 3D solo tiene sentido con un puntero que pueda apuntar, y nunca
// para quien pidio menos movimiento. Se reevalua si el usuario cambia
// cualquiera de las dos preferencias sin recargar.
const useInclinacion = () => {
  const [activa, setActiva] = useState(false);

  useEffect(() => {
    const fino = window.matchMedia('(hover: hover) and (pointer: fine)');
    const quieto = window.matchMedia('(prefers-reduced-motion: reduce)');
    const evaluar = () => setActiva(fino.matches && !quieto.matches);
    evaluar();
    fino.addEventListener('change', evaluar);
    quieto.addEventListener('change', evaluar);
    return () => {
      fino.removeEventListener('change', evaluar);
      quieto.removeEventListener('change', evaluar);
    };
  }, []);

  // Se escribe en el DOM directo y no en el estado: un mousemove dispara del
  // orden de 60 eventos por segundo y cada uno seria un render de React.
  const alMover = (e) => {
    if (!activa) return;
    const portada = e.currentTarget.querySelector('[data-portada]');
    if (!portada) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    portada.style.setProperty('--ry', `${(x * FACTOR).toFixed(2)}deg`);
    portada.style.setProperty('--rx', `${(-y * FACTOR).toFixed(2)}deg`);
    portada.style.setProperty('--z', String(ACERCAMIENTO));
  };

  const alSalir = (e) => {
    const portada = e.currentTarget.querySelector('[data-portada]');
    if (!portada) return;
    portada.style.setProperty('--rx', '0deg');
    portada.style.setProperty('--ry', '0deg');
    portada.style.setProperty('--z', '1');
  };

  return activa ? { onMouseMove: alMover, onMouseLeave: alSalir } : {};
};

// Sustituto real para cuando la portada no carga: sin esto el onError dejaba
// un <img> roto apuntando a un archivo que no existe.
const SinPortada = ({ nombre }) => (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: INK.secundaria,
      fontSize: '2rem',
      fontWeight: 500
    }}
  >
    {(nombre || '?').charAt(0).toUpperCase()}
  </Box>
);

const ProfileGrid = () => {
  // Si ya estuvimos aqui, las tarjetas se dibujan en el primer render en vez
  // de dejar la pantalla vacia mientras vuelve la red.
  const [profiles, setProfiles] = useState(() => perfilesCacheados() || []);
  const [loading, setLoading] = useState(() => !perfilesCacheados());
  const [error, setError] = useState(null);
  const [rotas, setRotas] = useState({});
  const [userRole, setUserRole] = useState(null);
  const { user, userRole: authUserRole } = useAuth();
  const navigate = useNavigate();
  const inclinacion = useInclinacion();

  // useLayoutEffect y no useEffect: corre antes del pintado, asi la grilla
  // aparece ya a la altura correcta. Con useEffect se veria un frame arriba de
  // todo y despues el salto.
  useLayoutEffect(() => {
    const y = scrollRecordado();
    if (y !== null && profiles.length > 0) window.scrollTo(0, y);
    // Solo al montar: despues el scroll es del usuario.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El rol se sincroniza aparte. Estaba dentro del efecto de carga, que dependia
  // de authUserRole: cuando el rol se resolvia, el efecto volvia a correr y
  // pedia las colecciones por segunda vez (medido: 123ms y 331ms seguidos).
  useEffect(() => {
    setUserRole(authUserRole);
  }, [authUserRole]);

  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await fetchProfiles();
        setProfiles(data || []);
      } catch (e) {
        console.error('Error fetching profiles:', e);
        // Con datos en pantalla, un fallo al refrescar no justifica vaciarla.
        if (!perfilesCacheados()) setError('Error al cargar los perfiles');
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, []);

  const abrirColeccion = (id) => {
    recordarScroll();
    // El perfil viaja con la navegacion para que la cabecera de la coleccion
    // se dibuje en el primer render en vez de esperar tres peticiones en serie.
    navigate(`/profile/${id}`, {
      state: { perfilPrevio: profiles.find((p) => p.id === id) }
    });
  };
  const crearColeccion = () => navigate('/create-profile');
  const editarColeccion = (e, id) => {
    e.stopPropagation();
    navigate(`/edit-profile/${id}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: 300 }}>
        <CircularProgress size={20} thickness={4} />
        <Typography sx={{ color: INK.secundaria }}>Loading collections</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>;
  }

  const puedeEditar = (profile) =>
    user && (userRole === 'admin' || profile.user_id === user.id);

  return (
    <Box sx={{ py: { xs: 1, sm: 4 } }}>
      {/* El titulo manda. Crear coleccion es la accion menos frecuente de la
          pantalla, asi que no puede ser el elemento mas pesado: en movil pierde
          la caja y queda como texto. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 2,
          mb: { xs: 3, sm: 5 }
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '1.375rem', sm: '1.75rem' },
              fontWeight: 500,
              letterSpacing: '-0.01em',
              color: INK.primaria
            }}
          >
            Collections
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: '0.8125rem', color: INK.secundaria }}>
            {profiles.length} {profiles.length === 1 ? 'collection' : 'collections'}
          </Typography>
        </Box>

        {user && (
          <Button
            variant="text"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={crearColeccion}
            sx={{
              flexShrink: 0,
              borderRadius: '8px',
              px: { xs: 0.75, sm: 1.5 },
              py: { xs: 0.5, sm: 1 },
              fontSize: '0.875rem',
              fontWeight: 500,
              color: { xs: INK.secundaria, sm: INK.primaria },
              border: { xs: '1px solid transparent', sm: `1px solid ${INK.borde}` },
              '&:hover': { backgroundColor: INK.hover, borderColor: { sm: 'rgba(255,255,255,0.18)' } }
            }}
          >
            New
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>
              collection
            </Box>
          </Button>
        )}
      </Box>

      {profiles.length === 0 ? (
        <Box sx={{ py: 10, maxWidth: '46ch' }}>
          <Typography sx={{ fontSize: '1.125rem', fontWeight: 500, color: INK.primaria, mb: 1 }}>
            No collections yet
          </Typography>
          <Typography sx={{ fontSize: '0.9375rem', color: INK.secundaria, mb: 3 }}>
            Create your first collection to start organizing your images.
          </Typography>
          {user && (
            <Button
              variant="text"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={crearColeccion}
              sx={{
                borderRadius: '8px',
                px: 1.5,
                py: 1,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: INK.primaria,
                border: `1px solid ${INK.borde}`,
                '&:hover': { backgroundColor: INK.hover, borderColor: 'rgba(255,255,255,0.18)' }
              }}
            >
              New collection
            </Button>
          )}
        </Box>
      ) : (
        // API de Grid de MUI v7: `size` reemplaza a `item` + xs/sm/md/lg/xl.
        // Con las props viejas el ancho no se calculaba y las columnas no alineaban.
        // El salto entre colecciones tiene que ganarle al mayor hueco interno
        // (los 12px de portada a nombre); si no, el texto se lee como si
        // perteneciera a la fila de abajo.
        <Grid
          container
          columnSpacing={{ xs: 1.5, sm: 3 }}
          rowSpacing={{ xs: 3, sm: 5 }}
        >
          {profiles.map((profile, index) => (
            <Grid key={profile.id} size={{ xs: 6, sm: 4, md: 3, lg: 2.4, xl: 2 }}>
              <Coleccion
                role="link"
                tabIndex={0}
                aria-label={`${profile.name}, ${profile.image_count || 0} ${profile.image_count === 1 ? 'image' : 'images'}`}
                onClick={() => abrirColeccion(profile.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    abrirColeccion(profile.id);
                  }
                }}
                {...inclinacion}
              >
                <Portada data-portada={profile.id}>
                  {rotas[profile.id] || !profile.cover_image ? (
                    <SinPortada nombre={profile.name} />
                  ) : (
                    <img
                      className="portada-media"
                      src={profile.cover_image}
                      alt=""
                      // Las primeras filas ya estan en pantalla: diferirlas deja
                      // el nombre flotando sobre un hueco mientras cargan.
                      loading={index < 10 ? 'eager' : 'lazy'}
                      fetchPriority={index < 5 ? 'high' : 'auto'}
                      decoding="async"
                      onError={() => setRotas((r) => ({ ...r, [profile.id]: true }))}
                    />
                  )}
                </Portada>

                {/* Nombre y conteo son una sola unidad: gap corto entre ellos,
                    gap largo (el rowSpacing del Grid) hacia la coleccion vecina.
                    Editar vive aqui y no sobre la portada: tapaba justamente
                    aquello que la coleccion muestra. */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      component="h2"
                      sx={{
                        fontSize: '0.9375rem',
                        fontWeight: 500,
                        lineHeight: 1.35,
                        color: INK.primaria,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {profile.name}
                    </Typography>
                    <Typography
                      sx={{ mt: 0.25, fontSize: '0.8125rem', lineHeight: 1.35, color: INK.secundaria }}
                    >
                      {profile.image_count || 0} {profile.image_count === 1 ? 'image' : 'images'}
                    </Typography>
                  </Box>

                  {puedeEditar(profile) && (
                    <Acciones className="acciones">
                      <IconButton
                        size="small"
                        aria-label={`Edit ${profile.name}`}
                        onClick={(e) => editarColeccion(e, profile.id)}
                        sx={{
                          width: 32,
                          height: 32,
                          mt: '-4px',
                          color: INK.secundaria,
                          '&:hover': { backgroundColor: INK.hover, color: INK.primaria }
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Acciones>
                  )}
                </Box>
              </Coleccion>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ProfileGrid;
